import { useEffect, useState } from 'react'
import { createClobClient } from '@/lib/clobClient'
import { usePmSigner } from './usePmSigner'
import { usePmWalletStore } from '@/store/pmWalletStore'
import type { ClobClient } from '@polymarket/clob-client'

/**
 * CLOB client состояние:
 *   idle    — кошелёк не подключён, инициализация не запускалась
 *   loading — запрос ClobClient.createOrDeriveApiKey() в полёте
 *   ready   — клиент готов, можно размещать ордера
 *   failed  — Polymarket CLOB отказал (часто: EOA не имеет инициализированного
 *             аккаунта — нужен первый ордер через polymarket.com)
 */
export type ClobStatus = 'idle' | 'loading' | 'ready' | 'failed'

interface CacheEntry {
  status: ClobStatus
  client: ClobClient | null
  error: string | null
  promise: Promise<void> | null
}

// Module-level cache. Keyed by EOA address (signer identity). Одна init-попытка
// на всё приложение — все consumers шарят один clob-клиент и статус. Это лечит
// retry-spam: раньше N consumers × StrictMode = N×2 одинаковых init'ов, сейчас
// один для всех.
const cache = new Map<string, CacheEntry>()
type Listener = (e: CacheEntry) => void
const listeners = new Map<string, Set<Listener>>()

function notify(key: string) {
  const entry = cache.get(key)
  if (!entry) return
  listeners.get(key)?.forEach((fn) => fn(entry))
}

function subscribe(key: string, fn: Listener): () => void {
  let set = listeners.get(key)
  if (!set) {
    set = new Set()
    listeners.set(key, set)
  }
  set.add(fn)
  return () => {
    set!.delete(fn)
    if (set!.size === 0) listeners.delete(key)
  }
}

async function ensureInit(
  key: string,
  signer: import('ethers').Signer,
  proxyAddress: string,
): Promise<void> {
  let entry = cache.get(key)
  if (entry && (entry.status === 'ready' || entry.status === 'failed' || entry.status === 'loading')) {
    return entry.promise ?? Promise.resolve()
  }
  entry = { status: 'loading', client: null, error: null, promise: null }
  cache.set(key, entry)
  const promise = (async () => {
    try {
      const c = await createClobClient(signer, proxyAddress, { signatureType: 1 })
      cache.set(key, { status: 'ready', client: c, error: null, promise: null })
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      cache.set(key, { status: 'failed', client: null, error: msg, promise: null })
      // Log once (не в console.error per consumer).
      console.warn('[useClobClient] CLOB init failed (won\'t retry):', msg)
    } finally {
      notify(key)
    }
  })()
  entry.promise = promise
  notify(key) // loading state
  return promise
}

function useClobCache(): CacheEntry & { key: string | null } {
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const pmWallet = usePmSigner()
  const key = activeWallet === 'pm' && pmWallet ? pmWallet.address.toLowerCase() : null

  const [, force] = useState(0)

  useEffect(() => {
    if (!key || !pmWallet) return
    const unsub = subscribe(key, () => force((n) => n + 1))
    void ensureInit(key, pmWallet.signer, pmWallet.proxyAddress)
    return unsub
  }, [key, pmWallet])

  const entry = key ? cache.get(key) : undefined
  return {
    key,
    status: entry?.status ?? 'idle',
    client: entry?.client ?? null,
    error: entry?.error ?? null,
    promise: entry?.promise ?? null,
  }
}

/** Returns the ClobClient when ready, null otherwise (backcompat API). */
export function useClobClient(): ClobClient | null {
  return useClobCache().client
}

/** Returns {status, error} for UI gating. */
export function useClobClientStatus(): { status: ClobStatus; error: string | null } {
  const { status, error } = useClobCache()
  return { status, error }
}

/** Manual reset — вызвать после reconnect / import-key, чтобы снять failed
 * статус и позволить новую попытку init. */
export function resetClobClientCache(): void {
  cache.clear()
  for (const set of listeners.values()) set.forEach((fn) => fn({ status: 'idle', client: null, error: null, promise: null }))
}
